import { FaCopy } from "react-icons/fa";
import { FaCheckCircle } from "react-icons/fa";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination, EffectFade } from "swiper/modules";
import { useState, useEffect } from "react";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/effect-fade";
import { Base_Url, fetchOffers } from "../../config/config";

export default function ImageSlider() {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copiedCode, setCopiedCode] = useState(null);

  useEffect(() => {
    const fetchOffer = async () => {
      const token = localStorage.getItem('token');
      const cus_id = localStorage.getItem('cus_id');

      try {
        setLoading(true);
        const response = await fetch(Base_Url + fetchOffers, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "cus_id": cus_id,
            "token": token
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();

        if (result.status === "SUCCESS" && result.data) {
          setOffers(result.data);
        } else {
          throw new Error(result.message || "Failed to fetch offers");
        }
      } catch (err) {
        setError(err.message);
        console.error("Error fetching offers:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchOffer();
  }, []);

  const handleCopyCode = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedCode(code);
      setTimeout(() => {
        setCopiedCode(null);
      }, 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  if (loading) {
    return (
      <div className="bg-gradient-to-br from-gray-100 to-gray-200 rounded-xl overflow-hidden h-56 flex items-center justify-center shadow-lg">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 font-medium">Loading amazing offers...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-xl overflow-hidden h-56 flex items-center justify-center shadow-lg">
        <div className="text-center px-6">
          <div className="text-red-500 text-5xl mb-4">⚠️</div>
          <p className="text-red-700 font-semibold text-lg">Oops! Something went wrong</p>
          <p className="text-red-600 mt-2">{error}</p>
        </div>
      </div>
    );
  }

  if (offers.length === 0) {
    return (
      <div className="bg-gradient-to-br from-gray-100 to-gray-200 rounded-xl overflow-hidden h-56 flex items-center justify-center shadow-lg">
        <div className="text-center">
          <div className="text-gray-400 text-6xl mb-4">🎁</div>
          <p className="text-gray-600 font-medium text-lg">No offers available at the moment</p>
          <p className="text-gray-500 text-sm mt-2">Check back soon for exciting deals!</p>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-xl overflow-hidden shadow-2xl min-h-32 relative">
      <Swiper
        modules={[Autoplay, Pagination, EffectFade]}
        spaceBetween={0}
        slidesPerView={1}
        loop={true}
        effect="fade"
        fadeEffect={{ crossFade: true }}
        autoplay={{
          delay: 4000,
          disableOnInteraction: false,
        }}
        pagination={{
          clickable: true,
          dynamicBullets: true,
        }}
        className="rounded-xl w-full h-full"
      >
        {offers.map((offer) => (
          <SwiperSlide key={offer.id}>
            <div className="relative w-full h-full group cursor-pointer">
              {/* Image */}
              <img
                src={Base_Url + offer.image}
                alt={`${offer.category} - ${offer.description}`}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                onError={(e) => {
                  e.target.src = "assets/images/placeholder.jpg";
                }}
              />

              {/* Gradient Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>

              {/* Content Overlay */}
              <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8 text-white transform transition-transform duration-300 group-hover:translate-y-[-8px]">
                {/* Badge */}
                <div className="inline-flex items-center gap-2 mb-3">
                  <span className="bg-gradient-to-r from-yellow-400 to-orange-500 text-black px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider shadow-lg">
                    {offer.discount_value}% OFF
                  </span>
                  <span className="bg-white/20 backdrop-blur-sm px-3 py-1.5 rounded-full text-xs font-semibold">
                    {offer.category}
                  </span>
                </div>

                {/* Description */}
                <div className="relative group/desc mb-2">
                  <h3 className="text-xl md:text-2xl font-bold drop-shadow-lg leading-tight break-words line-clamp-2 cursor-help">
                    {offer.description}
                  </h3>
                  {/* Tooltip on hover */}
                  <div className="absolute bottom-full left-0 mb-2 hidden group-hover/desc:block w-full max-w-md z-50 animate-fadeIn">
                    <div className="bg-black/95 backdrop-blur-sm text-white text-sm p-4 rounded-lg shadow-2xl border border-white/20">
                      <p className="leading-relaxed">{offer.description}</p>
                      <div className="absolute bottom-0 left-8 transform translate-y-1/2 rotate-45 w-3 h-3 bg-black/95 border-r border-b border-white/20"></div>
                    </div>
                  </div>
                </div>

                {/* Coupon Code */}
                <div className="flex items-center gap-3 mt-4">
                  <div className="bg-white/10 backdrop-blur-md border-2 border-white/30 rounded-lg px-3 py-1 flex items-center gap-2">
                    <span className="text-xs text-white/80 font-medium">CODE:</span>
                    <span className="text-lg font-bold tracking-wider">{offer.coupon_code}</span>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCopyCode(offer.coupon_code);
                    }}
                    className="bg-white text-gray-900 px-6 py-2  rounded-lg font-semibold text-sm hover:bg-gray-100 transition-colors duration-200 shadow-lg"
                  >
                    {copiedCode === offer.coupon_code ? (
                      <FaCheckCircle className="text-green-500 text-xl " />
                    ) : (
                      <FaCopy className="text-gray-600 text-xl hover:text-blue-600 cursor-pointer" />
                    )}
                  </button>
                </div>

                {/* Validity */}
                <p className="text-white/70 text-xs mt-3 font-medium">
                  Valid until {new Date(offer.end_date).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric'
                  })}
                </p>
              </div>

              {/* Corner Accent */}
              <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-bl from-yellow-400/30 to-transparent rounded-bl-full"></div>
            </div>
          </SwiperSlide>
        ))}
      </Swiper>

      {/* Custom Pagination Styles */}
      <style jsx>{`
        .swiper-pagination-bullet {
          background: white;
          opacity: 0.5;
          width: 10px;
          height: 10px;
        }
        .swiper-pagination-bullet-active {
          opacity: 1;
          background: white;
          width: 24px;
          border-radius: 5px;
        }
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(5px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.2s ease-out;
        }
      `}</style>
    </div>
  );
}